import { EGiveawayStatus } from "../../../enums";

export interface IDashboardData {
    totalUsers: number;
}

export interface IDashboardInvoicesData {
    totalInvoices: number;
    totalValue: number;
}

export interface IDashboardPendingInvoicesData {
    totalPendingInvoices: number;
}

export interface IDashboardInvoicesStatsData {
    totalValid: number;
    totalValue: number;
}

export interface IDashboardInvoicesByTypeItem {
    type: string;
    total: number;
    totalValid: number;
    totalError: number;
}

export interface IDashboardRegistrationHistoryItem {
    label: string;
    total: number;
}

export interface IDashboardInvoicesImportHistoryItem {
    label: string;
    total: number;
    totalValid: number;
}

export interface IDashboardTopCompanyItem {
    company: string;
    cpfCnpj: string;
    total: number;
    city: string;
    totalValid: number;
    totalAlert: number;
    totalError: number;
}

export interface IDashboardTopUserItem {
    userId: number;
    name: string;
    cpf: string;
    email: string;
    total: number;
    totalValid: number;
    totalAlert: number;
    totalError: number;
    totalTickets: number;
    totalValue: number;
}

export interface IDashboardSocialInstitutionItem {
    id: number;
    name: string;
    cnpj: string;
    active: boolean;
    totalUsers: number;
}

export interface IDashboardSocialInstitutionsData {
    total: number;
    totalActive: number;
    totalLinkedUsers: number;
    institutions: IDashboardSocialInstitutionItem[];
}

export interface IDashboardFinishedRaffle {
    id: number;
    kind: 'manual' | 'auto';
    description: string;
    status: string;
    dateOfRaffle: string;
    startPeriod: string;
    endPeriod: string;
    totalParticipants: number;
    totalTickets: number;
    totalWinners: number;
    totalPrizes: number;
    totalPrizeValue: number;
}

export interface IDashboardUpcomingRaffle {
    id: number;
    kind: 'manual' | 'auto';
    description: string;
    status: EGiveawayStatus;
    dateOfRaffle: string;
    startPeriod: string;
    endPeriod: string;
    totalTickets: number;
    totalParticipants: number;
    countyId?: number;
    countyName?: string;
}

class DashboardMapper {
    toDomain(persistence: any): IDashboardData {
        return {
            totalUsers: persistence?.total ?? 0,
        }
    }

    toInvoicesDomain(persistence: any): IDashboardInvoicesData {
        return {
            totalInvoices: persistence?.total ?? 0,
            totalValue: Number(persistence?.total_value ?? persistence?.totalValue ?? 0),
        }
    }

    toPendingInvoicesDomain(persistence: any): IDashboardPendingInvoicesData {
        return {
            totalPendingInvoices: persistence?.total ?? 0,
        }
    }

    toInvoicesStatsDomain(persistence: any): IDashboardInvoicesStatsData {
        return {
            totalValid: persistence?.total_valid ?? persistence?.totalValid ?? 0,
            totalValue: Number(persistence?.total_value ?? persistence?.totalValue ?? 0),
        }
    }

    toInvoicesByTypeDomain(persistence: any[]): IDashboardInvoicesByTypeItem[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            type: item.type,
            total: item.total ?? 0,
            totalValid: item.totalValid ?? item.total_valid ?? 0,
            totalError: item.totalError ?? item.total_error ?? 0,
        }));
    }

    toRegistrationHistoryDomain(persistence: any[]): IDashboardRegistrationHistoryItem[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            label: item.label,
            total: item.total ?? 0,
        }));
    }

    toInvoicesImportHistoryDomain(persistence: any[]): IDashboardInvoicesImportHistoryItem[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            label: item.label,
            total: item.total ?? 0,
            totalValid: item.total_valid ?? item.totalValid ?? 0,
        }));
    }

    toTopCompaniesDomain(persistence: any[]): IDashboardTopCompanyItem[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            company: item.company,
            cpfCnpj: item.cpfCnpj ?? item.cpf_cnpj ?? '',
            total: item.total ?? 0,
            city: item.city ?? '',
            totalValid: item.total_valid ?? item.totalValid ?? 0,
            totalAlert: item.total_alert ?? item.totalAlert ?? 0,
            totalError: item.total_error ?? item.totalError ?? 0,
        }));
    }

    toUpcomingRafflesDomain(persistence: any[]): IDashboardUpcomingRaffle[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            id: item.id,
            kind: item.kind,
            description: item.description,
            status: item.status,
            dateOfRaffle: item.dateOfRaffle ?? item.date_of_raffle,
            startPeriod: item.startPeriod ?? item.start_period,
            endPeriod: item.endPeriod ?? item.end_period,
            totalTickets: item.totalTickets ?? item.total_tickets ?? 0,
            totalParticipants: item.totalParticipants ?? item.total_participants ?? 0,
            countyId: item.countyId ?? item.county_id ?? item.county?.id,
            countyName: item.city ?? item.city ?? item.county?.name ?? item.city ?? item.county?.city ?? '',
        }));
    }

    toTopUsersDomain(persistence: any[]): IDashboardTopUserItem[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            userId: item.userId ?? item.user_id ?? 0,
            name: item.name ?? '',
            cpf: item.cpf ?? '',
            email: item.email ?? '',
            total: item.total ?? 0,
            totalValid: item.total_valid ?? item.totalValid ?? 0,
            totalAlert: item.total_alert ?? item.totalAlert ?? 0,
            totalError: item.total_error ?? item.totalError ?? 0,
            totalTickets: item.total_tickets ?? item.totalTickets ?? 0,
            totalValue: Number(item.total_value ?? item.totalValue ?? 0),
        }));
    }

    toSocialInstitutionsDomain(persistence: any): IDashboardSocialInstitutionsData {
        const list = Array.isArray(persistence?.institutions) ? persistence.institutions : [];
        return {
            total: persistence?.total ?? 0,
            totalActive: persistence?.total_active ?? persistence?.totalActive ?? 0,
            totalLinkedUsers: persistence?.total_linked_users ?? persistence?.totalLinkedUsers ?? 0,
            institutions: list.map((item: any) => ({
                id: item.id,
                name: item.name ?? '',
                cnpj: item.cnpj ?? '',
                active: Boolean(item.active),
                totalUsers: item.total_users ?? item.totalUsers ?? 0,
            })),
        };
    }

    toFinishedRafflesDomain(persistence: any[]): IDashboardFinishedRaffle[] {
        if (!Array.isArray(persistence)) return [];
        return persistence.map(item => ({
            id: item.id,
            kind: item.kind,
            description: item.description ?? '',
            status: item.status ?? 'Finalizado',
            dateOfRaffle: item.dateOfRaffle ?? item.date_of_raffle,
            startPeriod: item.startPeriod ?? item.start_period,
            endPeriod: item.endPeriod ?? item.end_period,
            totalParticipants: item.totalParticipants ?? item.total_participants ?? 0,
            totalTickets: item.totalTickets ?? item.total_tickets ?? 0,
            totalWinners: item.totalWinners ?? item.total_winners ?? 0,
            totalPrizes: item.totalPrizes ?? item.total_prizes ?? 0,
            totalPrizeValue: Number(item.totalPrizeValue ?? item.total_prize_value ?? 0),
        }));
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new DashboardMapper();
